/** @jsx jsx */
import { Button, Callout, Classes, Drawer, Intent, Position, Tab, Tabs } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { css, jsx } from '@emotion/core';
import { gql } from '@apollo/client';
import { Fragment, useState } from 'react';
import PersonIconList from '../../../components/PersonIconList';
import { ProspectListItemFragment } from '../../../generated/graphql';
import AttendsTab from './AttendsTab';
import CompanionMatchListItem from './CompanionMatchListItem';
import FutureSignupsTab from './FutureSignupsTab';
import MatchingTab from './MatchingTab';
import { getAgeInYears } from '../../../helpers/dateTimeUtils';
import Text from '../../../components/Text';
import TooltipTag from '../../../components/TooltipTag';
import ConflictingSignups from './ConflictingSignups';

type InfoDrawerProps = {
  signup: ProspectListItemFragment;
  isOpen: boolean;
  onClose: () => void;
  addLoneSoul: (soulSignupId: string, buysTicket: boolean) => void;
  addLoneCompanion: (companionSignupId: string) => void;
  addTeam: (companionSignupId: string, soulSignupId: string, soulBuysTicket: boolean) => void;
};

const InfoDrawer = ({ signup, addLoneSoul, addLoneCompanion, addTeam, isOpen, onClose }: InfoDrawerProps) => {
  const prospect = signup.soul || signup.companion!;
  const isSoul = !!signup.soul;
  const { user } = prospect;

  const [tabWidth, setTabWidth] = useState(isSoul ? '1200px' : '800px');
  const [activeTab, setActiveTab] = useState(isSoul ? 'matching' : 'attends');

  const tabSwitched = (tabId: any) => {
    if (tabId === 'matching') {
      setTabWidth('1200px');
    } else {
      setTabWidth('800px');
    }
    setActiveTab(tabId);
  };

  const addLone = () =>
    isSoul ? addLoneSoul(signup.id, signup.soul?.buys_ticket || false) : addLoneCompanion(signup.id);

  return (
    <Drawer
      title={
        <Fragment>
          <Text large>{user.display_name} </Text>
          {!!user.birthdate && (
            <Text muted large>
              {`(${getAgeInYears(user.birthdate).toString()}) `}
            </Text>
          )}
          {signup.is_first_attend && (
            <TooltipTag
              minimal
              intent={Intent.SUCCESS}
              label="Neu"
              tooltip="Erste Teilnahme"
              marginRight
              position={Position.BOTTOM}
            />
          )}
          <ConflictingSignups signups={signup.conflicting_signups} userName={user.display_name} />
          <PersonIconList person={prospect} position={Position.BOTTOM} />
        </Fragment>
      }
      position={Position.LEFT}
      size={tabWidth}
      isOpen={isOpen}
      onClose={onClose}
    >
      <div css={styles.container} className={Classes.DRAWER_BODY}>
        {!!signup.notes && (
          <Callout css={styles.callout} intent={Intent.PRIMARY} icon={null}>
            {signup.notes}
          </Callout>
        )}
        <Tabs id="infoTabs" selectedTabId={activeTab} css={styles.tabsContainer} onChange={tabSwitched}>
          {isSoul && (
            <Tab
              id="matching"
              title="Angemeldete Begleiter"
              panel={<MatchingTab soulSignup={signup} addTeam={addTeam} />}
            />
          )}
          <Tab id="attends" title="Teilnahmen" panel={<AttendsTab isSoul={isSoul} prospectId={prospect.id} />} />
          <Tab id="signups" title="Anmeldungen" panel={<FutureSignupsTab isSoul={isSoul} prospectId={prospect.id} />} />
        </Tabs>
      </div>
      <div className={Classes.DRAWER_FOOTER}>
        <Button
          icon={IconNames.SMALL_TICK}
          intent={Intent.SUCCESS}
          text={isSoul ? 'Ohne Begleitung auf die Teilnehmerliste' : 'Ohne Genießer auf die Teilnehmerliste'}
          css={styles.addLoneButton}
          onClick={addLone}
        />
      </div>
    </Drawer>
  );
};

InfoDrawer.fragments = {
  companionMatching: gql`
    fragment CompanionMatchingTab on Event {
      id
      signups {
        ...CompanionMatchListItem
      }
      location {
        id
        street
        postal_code
        city
        lat
        lng
      }
    }
    ${CompanionMatchListItem.fragments.companion}
  `,
};

export default InfoDrawer;

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: visible;
    padding-top: 16px;
  `,
  title: css`
    font-size: 16px;
    margin-right: 10px;
  `,
  tabsContainer: css`
    padding: 0 20px;
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;
    & div[role='tabpanel'][aria-hidden='false'] {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }
  `,
  tabPanel: css`
    margin: 0 -20px;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  `,
  callout: css`
    margin: 0 20px 16px;
    width: auto !important;
  `,
  addLoneButton: css`
    float: right;
  `,
};
